import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { navigate } from '@gumtree/shell/src/common/common-actions';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { Button, Icon } from '@gumtree/ui-library';
import { useModal } from '@gumtree/shell/src/use-modal';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import { setRequestId } from '@gumtree/shell/src/request-id';
import { SellerSkillItem } from '@gumtree/shared/src/model/listing.model';

import type { VipState, VipUseDispatch } from '@gumtree/vip/src/reducers';

// Selectors and other necessary imports from the original file might be needed here
// For example, if selectState is used by this button's logic directly or indirectly.
const selectState = (state: VipState) => ({
    adDetails: state.adDetails,
    userData: state.userData,
    // Add other necessary state slices if getLabel or handleContactByEmail depends on them
});

interface EmailContactButtonProps {
    replyEmail?: string;
    isCurrentUserSeller?: boolean;
    isFromMotorsFeed?: boolean;
    adId?: string;
    style?: any;
    trackEventPayload;
    // 新增的props用于Bark广告
    adSource?: string;
    imageUrl?: string;
    title?: string;
    thirdPartySellerId?: string;
    allSellerSkills?: SellerSkillItem[];
    updateRequestQuoteFormData?: {
        setProfile: (profile: string) => void;
        setTitle: (title: string) => void;
        setPreferredContactType: (preferredContactType: "quote" | "call") => void;
        setSellerId: (sellerId: string) => void;
        setAdvertId: (advertId: string) => void;
        setSellerSkills: (sellerSkills: any[]) => void;
        openModal: () => void;
    };
    requestId?: string;
}

const EmailContactButton: React.FC<EmailContactButtonProps> = ({
    replyEmail,
    isCurrentUserSeller,
    isFromMotorsFeed,
    adId,
    style, // 解构 style prop
    trackEventPayload,

    adSource,
    imageUrl,
    title,
    thirdPartySellerId,
    allSellerSkills,
    updateRequestQuoteFormData,
    requestId,
}) => {
    const { userData } = useSelector(selectState, shallowEqual);
    const { openDialog } = useModal();
    const userLoggedIn = !!userData && userData.userLoggedIn;
    const dispatch = useDispatch() as VipUseDispatch;
    const replyUrl = `/reply/${adId}${isCurrentUserSeller ? '?ius=true' : ''}`;

    const contactByEmail = async () => {
        trackPermutiveCustomEvent(window, PermutiveEventNames.R2S_EMAIL_BEGIN, {}, true);
        trackGA4PreNav<GA4.ContactSellerClickEvent>({
            event: 'contact_a_seller',
            contactASellerMethod: 'click email link',
            formValidation: 'success',
            listingPartner: isFromMotorsFeed ? 'motors' : undefined,
            listingDetails: trackEventPayload,
        });
        setRequestId(`/reply-confirmation/${adId}`);
        dispatch(navigate(replyUrl));
    };

    const handleContactByEmail = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        trackButtonClick();
        
        e.preventDefault();
        e.stopPropagation();

        if (adSource === 'Bark' && updateRequestQuoteFormData) {
            if (!userLoggedIn) {
                openDialog(e, LoginModalType.REQUEST_QUOTE, true);
            } else {
                updateRequestQuoteFormData.setProfile(imageUrl || '');
                updateRequestQuoteFormData.setTitle(title || '');
                updateRequestQuoteFormData.setSellerId(thirdPartySellerId || '');
                updateRequestQuoteFormData.setAdvertId(adId || '');
                updateRequestQuoteFormData.setSellerSkills(allSellerSkills || []);
                updateRequestQuoteFormData.setPreferredContactType('quote');
                updateRequestQuoteFormData.openModal();
            }
            return;
        }
        
        if (!userLoggedIn) {
            openDialog(e, LoginModalType.EMAIL, true, replyUrl);
            return;
        }
        
        await contactByEmail();
    };

    const trackButtonClick = () => {
        trackGA4Event<any>({
            event: 'ad_click_button_info',
            requestId,
            listingId: adId
        });
    }
    
    if (adSource !== 'Bark' && !replyEmail) {
        return null;
    }

    return (
        <Button
            noFollow
            fullWidth
            style={style}
            display='primary'
            // display='secondary'
            disabled={isCurrentUserSeller}
            onClick={handleContactByEmail}
            label='Request a quote'
            icon={<Icon type='sendMsg' />}
        />
    );
};

export default EmailContactButton;
